import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { PageAccessOverrides } from '@/auth/authorizations'
import { QUERY_KEY_CONSTANT } from '@/types/constants'
import { authenticatedApi } from '@/utils/api'

import { useConstant } from './useConstant'

const STORAGE_KEY = 'pageAccess'

// Cache the last-known overrides so the sidebar renders correctly before the
// constant query resolves.
export const readStoredPageAccess = (): PageAccessOverrides => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export const writeStoredPageAccess = (value: PageAccessOverrides): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // localStorage unavailable — fall back to the live query each load.
  }
}

/**
 * Overrides from the `pageAccess` constant. `isSettled` is true once the
 * query resolved (data or error); until then the cached value is returned.
 * On error the caller gets {} (code defaults), never the stale cache.
 */
export const usePageAccess = (): { overrides: PageAccessOverrides; isSettled: boolean } => {
  const { data, isFetched, isError } = useConstant({ id: STORAGE_KEY })
  if (isError) return { overrides: {}, isSettled: true }
  if (data?.value) return { overrides: data.value as PageAccessOverrides, isSettled: true }
  if (isFetched) return { overrides: {}, isSettled: true }
  return { overrides: readStoredPageAccess(), isSettled: false }
}

export type PageAccessUpdate = Record<string, { roles: string[] } | null>

async function updatePageAccess(body: PageAccessUpdate) {
  return authenticatedApi.put('constant/pageAccess', { json: body }).json()
}

export function useUpdatePageAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PageAccessUpdate) => updatePageAccess(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONSTANT] })
      notifications.show({ title: 'Pages updated', message: 'Page access has been updated', color: 'green' })
    },
    onError: () => {
      notifications.show({ title: 'Update failed', message: 'Failed to update page access', color: 'red' })
    },
  })
}
